/* WSNZ Database v4 runtime. Load after setting window.WSNZ_DATABASE_URL. */
(function () {
  "use strict";

  const api = window.WSNZ || {};
  let database = null;

  async function load(url) {
    const source = url || window.WSNZ_DATABASE_URL;

    if (!source) {
      throw new Error("WSNZ_DATABASE_URL is not configured.");
    }

    const response = await fetch(source, {
      credentials: "same-origin"
    });

    if (!response.ok) {
      throw new Error(
        `WSNZ database failed to load: ${response.status}`
      );
    }

    database = await response.json();

    try {
      const versionKey =
        database.configuration?.storage?.versionKey ||
        "wsnzDatabaseVersion";

      localStorage.setItem(
        versionKey,
        database.schemaVersion || "4.0.0"
      );
    } catch (error) {
      console.warn(
        "WSNZ: database version could not be stored.",
        error
      );
    }

    const readyEvent =
      database.configuration?.events?.databaseReady ||
      "wsnz:database-ready";

    window.dispatchEvent(
      new CustomEvent(readyEvent, {
        detail: { database }
      })
    );

    return database;
  }

  function requireDb() {
    if (!database) {
      throw new Error("WSNZ database is not loaded.");
    }

    return database;
  }

  function getDatabase() {
    return requireDb();
  }

  function getKingdom(id) {
    return requireDb().kingdoms?.[id] || null;
  }

  function getKingdoms() {
    const db = requireDb();
    const ids = db.orders?.kingdoms || [];

    return ids
      .map((id) => db.kingdoms?.[id])
      .filter(Boolean);
  }

  function getDiscoveryById(id) {
    return requireDb().discoveries?.[id] || null;
  }

  function getDiscoveries(filters = {}) {
    const db = requireDb();
    let ids = Array.isArray(db.orders?.discoveries)
      ? db.orders.discoveries.slice()
      : Object.keys(db.discoveries || {});

    if (filters.kingdomId) {
      ids =
        db.indexes?.discoveriesByKingdom?.[filters.kingdomId] ||
        ids.filter(
          (id) =>
            db.discoveries?.[id]?.kingdomId ===
            filters.kingdomId
        );
    }

    if (filters.type) {
      ids = ids.filter(
        (id) =>
          String(db.discoveries?.[id]?.type || "")
            .toLowerCase() ===
          String(filters.type).toLowerCase()
      );
    }

    if (filters.rarity) {
      ids = ids.filter(
        (id) =>
          String(db.discoveries?.[id]?.rarity || "")
            .toLowerCase() ===
          String(filters.rarity).toLowerCase()
      );
    }

    return ids
      .map((id) => db.discoveries?.[id])
      .filter(Boolean);
  }

  function getJournal(id) {
    return requireDb().journals?.[id] || null;
  }

  function getJournalsByKingdom(kingdomId) {
    const db = requireDb();

    const ids =
      db.indexes?.journalsByKingdom?.[kingdomId] ||
      Object.keys(db.journals || {}).filter(
        (id) =>
          db.journals?.[id]?.kingdomId === kingdomId
      );

    return ids
      .map((id) => db.journals?.[id])
      .filter(Boolean);
  }

  function getUnlockedIds() {
    const db = requireDb();

    try {
      const storageKey =
        db.configuration?.storage
          ?.unlockedDiscoveriesKey ||
        "wsNzUnlockedDiscoveries";

      const saved =
        localStorage.getItem(storageKey) || "[]";

      const parsed = JSON.parse(saved);

      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn(
        "WSNZ: unlocked discovery storage is unavailable.",
        error
      );

      return [];
    }
  }

  function isUnlocked(id) {
    return getUnlockedIds().includes(id);
  }

  function unlockDiscovery(id) {
    const db = requireDb();
    const discovery = db.discoveries?.[id];

    if (!discovery) {
      return false;
    }

    const ids = getUnlockedIds();

    if (ids.includes(id)) {
      return false;
    }

    ids.push(id);

    try {
      const storageKey =
        db.configuration?.storage
          ?.unlockedDiscoveriesKey ||
        "wsNzUnlockedDiscoveries";

      localStorage.setItem(
        storageKey,
        JSON.stringify(ids)
      );
    } catch (error) {
      console.warn(
        "WSNZ: discovery unlock could not be saved.",
        error
      );

      return false;
    }

    const unlockEvent =
      db.configuration?.events?.discoveryUnlocked ||
      "wsnz:discovery-unlocked";

    window.dispatchEvent(
      new CustomEvent(unlockEvent, {
        detail: {
          id,
          discovery
        }
      })
    );

    return true;
  }

  function getProgress() {
    const db = requireDb();

    const total =
      db.orders?.discoveries?.length ||
      Object.keys(db.discoveries || {}).length;

    const found = getUnlockedIds().filter(
      (id) => db.discoveries?.[id]
    ).length;

    return {
      total,
      found,
      hidden: Math.max(total - found, 0),
      percent: total
        ? Math.round((found / total) * 100)
        : 0
    };
  }

  function getGameplayProfile(id, profile = "pathfinder") {
    const discovery = getDiscoveryById(id);

    if (!discovery) {
      return null;
    }

    return (
      discovery.gameplay?.profiles?.[profile] ||
      discovery.gameplay?.[profile] ||
      null
    );
  }

  Object.assign(api, {
    load,
    getDatabase,
    getKingdom,
    getKingdoms,
    getDiscoveryById,
    getDiscoveries,
    getJournal,
    getJournalsByKingdom,
    getUnlockedIds,
    isUnlocked,
    unlockDiscovery,
    getProgress,
    getGameplayProfile
  });

  window.WSNZ = api;
})();
